import React, { useEffect, useState } from "react";
import { Bounce, toast } from 'react-toastify';
import Checkbox from "../CheckboxComponent";
import { ISkill } from "../../model/Skill.model";
import { ISkillLevel } from "../../model/SkillLevel.model";
import useFetchWithMsal from "../../hooks/useFetchWithMsal";
import { protectedResources } from "../../authConfig";
import { IEmployeeProgressionLevel } from "../../model/EmployeeProgressionLevel.model";
import "./SkillLevelCheckboxes.scss";

interface SkillLevelCheckboxesProps {
  level: ISkillLevel;
  skill: ISkill;
  skillLevels: ISkillLevel[];
  isManager: boolean;
  currentUpn: string | undefined;
  employeeProgressionLevels: any[];
  employeeProgressionLevelsLength: number;
  allReadonly?: boolean;
  onMasteredAdded?: (event: IEmployeeProgressionLevel, skill: ISkill, level: ISkillLevel) => void;
  onMasteredChange?: (event: React.ChangeEvent<HTMLInputElement>, skill: ISkill, level: ISkillLevel) => void;
  onMasteredRemoved?: (event: string, skill: ISkill, level: ISkillLevel) => void;
}

const SkillLevelCheckboxes: React.FC<SkillLevelCheckboxesProps> = ({
  level,
  skill,
  skillLevels,
  isManager,
  currentUpn,
  employeeProgressionLevels,
  employeeProgressionLevelsLength,
  allReadonly: allReadonly = false,
  onMasteredAdded = () => {},
  onMasteredChange = () => {},
  onMasteredRemoved = () => {},
}) => {
  const { execute } = useFetchWithMsal({
    scopes: protectedResources.apiCFSkillPath.scopes.write,
  });

  // POST a new EmployeeProgressionLevel
  const postEmployeeProgressionLevel = async (progressionLevel: any, skill: ISkill, level: ISkillLevel) => {
    try {
      if (isManager) {
        progressionLevel.userPrincipalName = currentUpn;
      }
      await execute("POST", "/api/EmpProg/addEmpProgLevel", progressionLevel).then((res:any) => {
        if (res.stage==="M") {
          toast.success('Great Job! Your manager will be notified to discuss your skill(s) accomplishments.');
        }
        onMasteredAdded(res, skill, level);
      });

    } catch (err) {
      // handle error as needed
      console.error("Error posting EmployeeProgressionLevel", err);
    }
  };

  // DELETE an EmployeeProgressionLevel
  const deleteEmployeeProgressionLevel = async (progressionLevelId: string) => {
    try {
      await execute("DELETE", `/api/EmpProg/deleteEmpProgLevel/${progressionLevelId}`);
      onMasteredRemoved(progressionLevelId, skill, level);
    } catch (err) {
      // handle error as needed
      console.error("Error deleting EmployeeProgressionLevel", err);
    }
  };

  const isMastered = (level: ISkillLevel, skill: ISkill) => {
    var match = employeeProgressionLevels.find(
      (plevel) => plevel.skillId === skill.SkillId && plevel.skillLevelId === level.skillLevelId
    );
    return match != null;
  };
  const isMasteredConfirmed = (level: ISkillLevel, skill: ISkill) => {
    var match = employeeProgressionLevels.find(
      (plevel) => plevel.skillId === skill.SkillId && plevel.skillLevelId === level.skillLevelId
    );
    return match != null && match.stage === "MC";
  };

  const isReadonlyM = (level: ISkillLevel, skill: ISkill) => {
    var match = employeeProgressionLevels.find(
      (plevel) => plevel.skillId === skill.SkillId && plevel.skillLevelId === level.skillLevelId
    );
    return match === null && match.stage !== "MC";
  };
  const isReadonlyC = (level: ISkillLevel, skill: ISkill) => {
    var match = employeeProgressionLevels.find(
      (plevel) => plevel.skillId === skill.SkillId && plevel.skillLevelId === level.skillLevelId
    );
    return match == null;
  };
  const HandleonMasteredChange = (event: any, skill: ISkill, level: ISkillLevel) => {
    if (event.target.checked) {
      postEmployeeProgressionLevel({
        skillId: skill.SkillId,
        skillLevelId: level.skillLevelId,
        stage: "M"
      }, skill, level);
    } else {
      var match = employeeProgressionLevels.find(
        (plevel) => plevel.skillId === skill.SkillId && plevel.skillLevelId === level.skillLevelId
      );
      if (match) {
        deleteEmployeeProgressionLevel(match.employeeProgressionLevelId);
      }
    }
  };
  const HandleManagerConfirmClick = async (event: any, skill: ISkill, level: ISkillLevel) => {
    // Handle manager confirmation logic here
    // For example, you might want to update the stage to "MC" for Manager Confirmed
    var match = employeeProgressionLevels.find(
      (plevel) => plevel.skillId === skill.SkillId && plevel.skillLevelId === level.skillLevelId
    );
    if (match) {
      // Update the stage to "MC"
      match.stage = (event.target.checked) ? "MC" : "M";
      try {
        await execute("PUT", "/api/EmpProg/updateEmpProgLevel", match).then((res: any) => {
          onMasteredChange(res, skill, level);
        });

      } catch (err) {
        // handle error as needed
        console.error("Error posting EmployeeProgressionLevel", err);
      }
      //onMasteredChange(event, skill, level);
    }
  }
  const [canCheckMastered, setCanCheckMastered] = useState(false);
  const [readOnlyMastered, setReadOnlyMastered] = useState(false);

  useEffect(() => {
    let allowed = false;
    const indexNumber = level.indexNumber;
    if (indexNumber === 1) {
      allowed = true;
    } else {
      const previousLevel = skillLevels.find((l) => l.indexNumber === indexNumber - 1);
      if (previousLevel != null) {
        const previousMastered = isMastered(previousLevel, skill);
        if (previousMastered) allowed = true;
      }
    }
    setCanCheckMastered(allowed);
    // eslint-disable-next-line
  }, [level, skill, skillLevels, employeeProgressionLevels]);

  useEffect(() => {
    let readOnly = false;
    const indexNumber = level.indexNumber;
    const nextLevel = skillLevels.find(x => x.indexNumber > indexNumber);
    if (nextLevel) {
      const nextMastered = isMastered(nextLevel, skill);
      console.log("Next Level:", nextLevel);
      if (nextMastered) {
        readOnly = true;
      }
    }
    setReadOnlyMastered(readOnly);
    // eslint-disable-next-line
  }, [level, skill, skillLevels, employeeProgressionLevels, canCheckMastered]);

  const generateUniqueId = (): string => {
    return crypto.randomUUID();
  };

  /* #region Classes */

  const managerMasterCheckboxClasses = (level: ISkillLevel, skill: ISkill) => {
    const classes = ['checkbox-container'];
    if (isMastered(level, skill)) {
      classes.push("is-checked");
    }
    if (allReadonly || isReadonlyC(level, skill)) {
      classes.push("is-read-only");
    }
    return classes.join(" ");
  };
  const managerConfirmCheckboxClasses = (level: ISkillLevel, skill: ISkill) => {
    const classes = ['checkbox-container'];
    if (isMasteredConfirmed(level, skill)) {
      classes.push("is-checked");
    }
    if (allReadonly || isReadonlyC(level, skill)) {
      classes.push("is-read-only");
    }
    return classes.join(" ");
  };

  const nonManagerCheckboxClasses = (level: ISkillLevel, skill: ISkill) => {
    const classes = ["checkbox-container"];
    if (isMastered(level, skill)) {
      classes.push("is-checked");
    }
    if(allReadonly){
      classes.push("is-read-only");
    }
    return classes.join(" ");
  }

  /* #endregion Classes */

  return (
    <div className="skill-level-checkboxes">
      {!isManager && canCheckMastered && (
        <div className="dis-flex dis-flex-aln-center gap-1"><span className={nonManagerCheckboxClasses(level, skill)}>
          <Checkbox
            label="Mastered"
            isSelected={isMastered(level, skill)}
            isReadOnly={allReadonly || isMasteredConfirmed(level, skill) || readOnlyMastered}
            id={generateUniqueId()}
            onCheckboxChange={(event) => HandleonMasteredChange(event, skill, level)}
          />
        </span>
        <span className={managerConfirmCheckboxClasses(level, skill)}>{isMasteredConfirmed(level, skill) && (
          <span className="confirmed-label"><label className="mastered-label form-check-label">Confirmed</label></span>)}</span>
        </div>
      )}
      {isManager && isMastered(level, skill) && (
        <div className="dis-flex dis-flex-aln-center gap-1">
          <span  className={managerMasterCheckboxClasses(level, skill)}>
          <Checkbox
            label="Mastered"
            isSelected={isMastered(level, skill)}
            isReadOnly={allReadonly || isMasteredConfirmed(level, skill) || readOnlyMastered}
            id={generateUniqueId()}
            onCheckboxChange={(event) => HandleonMasteredChange(event, skill, level)}
          />
          </span>
          <span className={managerConfirmCheckboxClasses(level, skill)}>
            <Checkbox
              label="Manager Confirmed"
              isSelected={isMasteredConfirmed(level, skill)}
              isReadOnly={allReadonly || isReadonlyC(level, skill)}
              id={generateUniqueId()}
              onCheckboxChange={(event) => HandleManagerConfirmClick(event, skill, level)}
            />
          </span>
        </div>
      )}
      {isManager && canCheckMastered && !isMastered(level, skill) && (
        <div className="dis-flex dis-flex-aln-center gap-1"><span className={nonManagerCheckboxClasses(level, skill)}>
          <Checkbox
            label="Mastered"
            isSelected={isMastered(level, skill)}
            isReadOnly={allReadonly || isMasteredConfirmed(level, skill) || readOnlyMastered}
            id={generateUniqueId()}
            onCheckboxChange={(event) => HandleonMasteredChange(event, skill, level)}
          />
        </span>
        </div>
      )}
    </div>
  );
};

export default SkillLevelCheckboxes;